<template>
  <div>
    <el-form
      ref="editForm"
      :model="editMessage"
      :rules="rules"
      class="e-dialog__new"
    >
      <div v-if="sending" class="e-dialog__loading">
        <loading />
      </div>

      <el-input
        v-model="editMessage.subject"
        class="e-dialog__new-subject"
        :placeholder="`${$t('system.subject')}...`"
      />

      <div class="e-dialog__answer-field">
        <TiptapEditor
          v-model="editMessage.body"
          class="message-editor-tiptap"
          :placeholder="$t('system.writeAMessage')"
          :actions="editorOptions"
          @change="$emit('change')"
        />

        <div
          :class="{
            'e-dialog__charcount--alert': maxCharsExceeded
          }"
          class="e-dialog__charcount"
        >
          {{ charCountString }}
        </div>

        <transition name="fade-up">
          <div v-if="autoSaved" class="e-dialog__autosave">
            <IconCheck role="presentation" />
            <span>{{ $t('system.draft.autosaved') }}</span>
          </div>
        </transition>

        <transition name="fade-up">
          <div v-if="autoSaving" class="e-dialog__autosave">
            <i class="el-icon-loading" />
            <span>{{ $t('system.draft.autosaving') }}</span>
          </div>
        </transition>
      </div>

      <footer class="e-dialog__footer">
        <!-- Cancel -->
        <el-button
          class="e-btn--delete e-btn--ghost"
          @click="$emit('cancel')"
          :aria-label="$t('system.cancel')"
        >
          <IconClose role="presentation" />
          <span class="e-mobile-hidden">
            {{ $t('system.cancel') }}
          </span>
        </el-button>

        <!-- Delete -->
        <el-button
          v-if="editMessage.id"
          class="e-btn--delete e-btn--ghost e-btn--thin"
          :aria-label="$t('system.delete')"
          @click="onDelete"
        >
          <IconTrash role="presentation" />
          <span class="e-mobile-hidden">
            {{ $t('system.delete') }}
          </span>
        </el-button>
        <!-- /Delete -->

        <el-tooltip>
          <div slot="content" v-html="submitTooltip" />
            <el-button
            class="e-btn--send e-btn e-btn--thin"
              :disabled="
                autoSaving ||
                  editMessage.subject.length === 0 ||
                  editMessage.body.length === 0
              "
              type="tertiary"
              @click.prevent="submitForm"
            >
              {{ $t('system.sendMessage') }}
              <IconSend role="presentation" />
            </el-button>
        </el-tooltip>
      </footer>
    </el-form>

    <AbsencePrompt
      v-if="currentDialog"
      :confirm-is-open="absenceDialogOpen"
      :absent-until="currentDialog.counselorsAbsentUntil"
      :forward-loading="absenceForwardLoading"
      :wait-loading="absenceWaitLoading"
      @close-dialog="absenceDialogOpen = false"
      @close-absent-dialog="closeAbsentDialog"
    />

    <DeletedPrompt
      v-if="currentDialog"
      :show="deletedPromptOpen"
      @close-dialog="closeDeletedPrompt"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import TiptapEditor from '../../../shared/TiptapEditor'
import Loading from 'atoms/Loading/Loading'
import AbsencePrompt from 'molecules/AbsencePrompt/AbsencePrompt'
import DeletedPrompt from 'molecules/DeletedPrompt/DeletedPrompt'

// Icons
import IconTrash from 'assets/icons/trashcan.svg'
import IconSend from 'assets/icons/send.svg'
import IconClose from 'assets/icons/close.svg'
import IconCheck from 'assets/icons/arrows-circle-check.svg'

export default {
  name: 'MessageEditor',

  components: {
    AbsencePrompt,
    DeletedPrompt,
    IconCheck,
    IconClose,
    IconSend,
    IconTrash,
    Loading,
    TiptapEditor
  },

  props: {
    editMessage: {
      type: Object,
      default: () => {}
    },
    saveStatus: {
      type: String,
      default: 'unsaved'
    }
  },

  data() {
    return {
      maxChars: 10000,
      absenceDialogOpen: false,
      deletedPromptOpen: false,
      absenceForwardLoading: false,
      absenceWaitLoading: false,
      rules: {
        subject: [
          {
            required: true,
            message: this.$t('system.message.subjectEmpty'),
            trigger: 'blur'
          }
        ],
        body: [
          {
            required: true,
            message: this.$t('system.message.empty'),
            trigger: 'blur'
          }
        ]
      },
      editorOptions: [
        {
          name: 'bold',
          icon: '<b>F</b>',
          title: this.$t('system.editor.bold')
        },
        {
          name: 'italic',
          icon: '<i>K</i>',
          title: this.$t('system.editor.italic')
        },
        {
          name: 'underline',
          icon: '<u>U</u>',
          title: this.$t('system.editor.underline')
        }
      ]
    }
  },

  computed: {
    ...mapGetters(['currentDialog']),

    charCountString() {
      return `${this.charCount} / ${this.maxChars}`
    },
    charCount() {
      const text = this.editMessage.body
      const div = document.createElement('div')
      div.innerHTML = text
      return div.innerText.length
    },

    maxCharsExceeded() {
      return this.charCount > this.maxChars
    },

    submitTooltip() {
      if (this.editMessage.subject === '' && this.editMessage.body === '') {
        return `${this.$t('system.message.subjectEmpty')}<br>${this.$t(
          'system.message.empty'
        )}`
      }
      if (this.editMessage.subject === '' && this.editMessage.body !== '') {
        return `${this.$t('system.message.subjectEmpty')}`
      }
      if (this.editMessage.subject !== '' && this.editMessage.body === '') {
        return `${this.$t('system.message.empty')}`
      }
      return `${this.$t('system.sendMessage')}`
    },

    autoSaving() {
      return this.saveStatus === 'autosaving'
    },

    autoSaved() {
      return this.saveStatus === 'autoSaved'
    },

    sending() {
      return this.saveStatus === 'sending'
    }
  },

  mounted() {
    if (
      this.currentDialog &&
      this.currentDialog.counselorsAbsentUntil !== null
    ) {
      this.absenceDialogOpen = true
    }
    if (
      this.currentDialog &&
      (this.currentDialog.counselorWasDeleted ||
        this.currentDialog.emailCounselorRemoved)
    ) {
      this.deletedPromptOpen = true
    }
  },
  watch: {
    currentDialog: function(newVal, oldVal) {
      const showDeleted = d => d && (d.counselorWasDeleted || d.emailCounselorRemoved)
      if (showDeleted(newVal) && !showDeleted(oldVal) && this.deletedPromptOpen === false) {
        this.deletedPromptOpen = true
      }
    }
  },
  methods: {
    submitForm() {
      this.$refs.editForm.validate(valid => {
        if (valid) {
          this.$emit('send')
        }
      })
    },

    onDelete() {
      const message = this.$t('messages.dialog.deleteDraft')
      this.$confirm(message, this.$t('system.warning'), {
        confirmButtonText: this.$t('system.confirm'),
        cancelButtonText: this.$t('system.cancel'),
        type: 'warning'
      }).then(() => this.$emit('delete'))
    },

    closeAbsentDialog(forward) {
      this.absenceForwardLoading = forward === true
      this.absenceWaitLoading = forward !== true
      this.editMessage.forwardToCounselor = forward
      this.loading = false
      this.absenceDialogOpen = false
      // this.sendMessage()
    },
    closeDeletedPrompt() {
      this.deletedPromptOpen = false
      this.$emit('cancel')
    }
  }
}
</script>

<style lang="scss">
@import '_assets/messageeditor';
.tiptap-editor.message-editor-tiptap
  .tiptap-editor__content--editor
  .ProseMirror {
  @include break(l) {
    height: 300px;
  }
}
</style>
